import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Gift, CheckCircle2, MapPin, Mail, StickyNote } from 'lucide-react';

const RewardClaimsPanel = () => {
  const { api } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchClaims = useCallback(async () => {
    try {
      const res = await api.get('/gamification/admin/reward-claims');
      setClaims(res.data || []);
    } catch (error) {
      toast.error('Impossible de charger les réclamations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const markFulfilled = async (claim) => {
    try {
      setProcessing(claim.id);
      await api.put(`/gamification/admin/reward-claims/${claim.id}/fulfill`);
      toast.success('Récompense marquée comme traitée');
      fetchClaims();
    } catch (error) {
      toast.error('Erreur lors du traitement');
    } finally {
      setProcessing(null);
    }
  };

  const pending = claims.filter((c) => !c.fulfilled);
  const done = claims.filter((c) => c.fulfilled);

  const ClaimCard = ({ claim }) => (
    <Card className="card-base" data-testid={`reward-claim-${claim.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{claim.reward?.icon || '🎁'}</span>
              <div>
                <p className="font-semibold">{claim.reward?.title}</p>
                <p className="text-sm text-muted-foreground">
                  Niveau {claim.level} — {claim.user_name} ({claim.user_email})
                </p>
              </div>
            </div>
            <div className="space-y-1 mt-2 text-sm text-muted-foreground">
              {claim.address && (
                <p className="flex items-start gap-1"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> {claim.address}</p>
              )}
              {(claim.recipient_name || claim.recipient_email) && (
                <p className="flex items-start gap-1"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" /> Destinataire : {claim.recipient_name} {claim.recipient_email}</p>
              )}
              {claim.notes && (
                <p className="flex items-start gap-1"><StickyNote className="w-4 h-4 mt-0.5 flex-shrink-0" /> {claim.notes}</p>
              )}
              {claim.claimed_at && (
                <p className="text-xs">Réclamée le {new Date(claim.claimed_at).toLocaleDateString('fr-FR')}</p>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            {claim.fulfilled ? (
              <Badge className="bg-green-500/20 text-green-600">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Traitée
              </Badge>
            ) : (
              <Button
                size="sm"
                onClick={() => markFulfilled(claim)}
                disabled={processing === claim.id}
                data-testid={`fulfill-claim-btn-${claim.id}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {processing === claim.id ? 'Traitement...' : 'Marquer comme traitée'}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) return <p className="text-muted-foreground">Chargement...</p>;

  return (
    <div className="space-y-6" data-testid="reward-claims-panel">
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            Récompenses réclamées ({pending.length} à traiter)
          </CardTitle>
          <CardDescription>
            Les vrais cadeaux (box, coaching, drone...) réclamés par les membres. Les récompenses esthétiques (badges, titres, cadres) sont validées automatiquement et n'apparaissent pas ici.
          </CardDescription>
        </CardHeader>
      </Card>

      {pending.length === 0 && done.length === 0 && (
        <p className="text-muted-foreground text-center py-8">Aucune récompense réclamée pour le moment.</p>
      )}

      {pending.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase">À traiter</h3>
          {pending.map((c) => <ClaimCard key={c.id} claim={c} />)}
        </div>
      )}

      {done.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase">Historique traité</h3>
          {done.map((c) => <ClaimCard key={c.id} claim={c} />)}
        </div>
      )}
    </div>
  );
};

export default RewardClaimsPanel;
